/* ============================================================================
   color-swatches.js — Auto-detect sibling colour variants of the same fabric
   on the product page and render them as clickable coloured circles.

   Ridh fabrics follow the pattern:
     "{code} {Fabric name} · {code} {COLOUR NAME} {supplier}"
   e.g. "56751 Pure Chanderi 850 · 56751 RANI PINK FM"

   We extract the numeric base code from the current product's title, hit
   Shopify's public /search/suggest.json for other products sharing that
   base code, extract each sibling's colour name from the title, then render
   the circles pointing at each sibling's product page.
   ========================================================================== */
(function () {
  'use strict';

  var container = document.querySelector('[data-colour-siblings]');
  if (!container) return;

  var currentHandle = container.dataset.handle || '';
  var title = container.dataset.title || '';
  if (!title) return;

  // Colour name → CSS colour / hex mapping. Only entries that CSS doesn't
  // already know natively (or where we want a specific studio-safe tone).
  var COLOUR_MAP = {
    // Reds
    'red': '#C0392B', 'maroon': '#7B1F1F', 'wine': '#5C1A28', 'burgundy': '#722F37',
    'crimson': '#C42B4A', 'rani pink': '#E91E63', 'rani': '#E91E63',
    'onion': '#CD8896', 'onion pink': '#CD8896', 'brick': '#8B4513', 'rust': '#B7410E',
    'coral': '#FF7F50',
    // Pinks
    'pink': '#F48FB1', 'baby pink': '#F5B7CD', 'light pink': '#FFB6C1', 'hot pink': '#E91E63',
    'peach': '#FFB593', 'light peach': '#FFDAB9', 'peach pink': '#FBC4B5',
    'blush': '#F5B5A5', 'rose': '#E5677D', 'salmon': '#FA8072',
    // Oranges
    'orange': '#F39C12', 'mustard': '#D4A017', 'ochre': '#CC7722',
    'terracotta': '#B25835', 'amber': '#FFBF00',
    // Yellows
    'yellow': '#F1C40F', 'lemon': '#F0E68C', 'butter': '#F5E6A8', 'gold': '#B8923F',
    'golden': '#C9A227', 'sunshine': '#FFD700',
    // Greens
    'green': '#4CAF50', 'light green': '#8BC34A', 'dark green': '#1B5E20',
    'seagreen': '#2E8B57', 'sea green': '#2E8B57', 'mint': '#98D9B8', 'sage': '#B2C3A3',
    'olive': '#808000', 'emerald': '#046A38', 'teal': '#008080', 'mehandi': '#8A9A5B',
    'light mehandi': '#B5C68C', 'forest': '#228B22', 'bottle green': '#006A4E',
    // Blues
    'blue': '#3498DB', 'sky blue': '#87CEEB', 'sky': '#87CEEB', 'light blue': '#ADD8E6',
    'dark blue': '#0A1F44', 'navy': '#001F3F', 'royal blue': '#4169E1',
    'indigo': '#4B0082', 'cobalt': '#0047AB', 'turquoise': '#40E0D0',
    'baby blue': '#B0DDEB', 'powder blue': '#B0E0E6', 'midnight': '#191970',
    // Purples
    'purple': '#8E44AD', 'violet': '#8B00FF', 'lavender': '#B39DDB',
    'plum': '#8E4585', 'mauve': '#B784A7', 'lilac': '#C8A2C8',
    // Neutrals
    'white': '#F8F8F5', 'off white': '#F5EFE6', 'off-white': '#F5EFE6', 'ivory': '#FFFFF0',
    'cream': '#FFFDD0', 'beige': '#F5F5DC', 'sand': '#E6D5B8', 'nude': '#E3BC9A',
    'tan': '#D2B48C', 'khaki': '#C3B091', 'grey': '#95A5A6', 'gray': '#95A5A6',
    'silver': '#C0C0C0', 'ash': '#B2BEB5', 'stone': '#8D8378', 'charcoal': '#36454F',
    'black': '#1A1614', 'brown': '#8B4513', 'chocolate': '#5D3A1F', 'coffee': '#6F4E37'
  };

  // Extract base code (2-6 digit number) and fabric-name.
  // Ridh titles are usually "{base} {Fabric name} · {base} {COLOUR} {supplier}"
  // but a few use "{Fabric name} · {base} {COLOUR} {supplier}" — no base
  // before the dot. Try before-dot first, fall back to after-dot.
  function parseTitle(t) {
    var parts = t.split('·');
    var beforeDot = (parts[0] || '').trim();
    var afterDot = (parts[1] || '').trim();
    // Prefer base from beforeDot; otherwise take the first numeric run from afterDot
    var baseMatch = beforeDot.match(/\b(\d{2,})\b/);
    var base;
    var fabric;
    if (baseMatch) {
      base = baseMatch[1];
      fabric = beforeDot.replace(base, '').trim().replace(/\s+\d+\s*$/, '').trim();
    } else {
      baseMatch = afterDot.match(/\b(\d{2,})\b/);
      if (!baseMatch) return null;
      base = baseMatch[1];
      fabric = beforeDot; // whole beforeDot is the fabric family
    }
    return { base: base, fabric: fabric };
  }

  // Extract the colour name from a sibling product title.
  // Given "56751 Pure Chanderi 850 · 56751 RANI PINK FM" → "RANI PINK"
  // Given "142736 Chanderi Border 875 · 142736 LIGHT MEHANDI ICSK" → "LIGHT MEHANDI"
  function extractColourFromTitle(t, base) {
    var afterDot = (t.split('·')[1] || '').trim();
    // Strip leading base code
    afterDot = afterDot.replace(new RegExp('^' + base + '\\s+'), '');
    afterDot = afterDot.replace(/^\d+\s+/, ''); // stray leading digits (variant sub-code)
    // Strip ONE trailing all-caps supplier code (2-6 letters). This is the
    // last word — everything before it is the colour name(s).
    afterDot = afterDot.replace(/\s+[A-Z]{2,6}\s*$/, '');
    // Ignore if what's left is a pure digit run (no colour name)
    if (!afterDot || /^\d+$/.test(afterDot)) return null;
    return afterDot.trim();
  }

  function colourToCss(colourName) {
    if (!colourName) return '#B8923F';
    var lc = colourName.toLowerCase().trim();
    if (COLOUR_MAP[lc]) return COLOUR_MAP[lc];
    // Try last word (e.g. "PEACH PINK" → "pink")
    var words = lc.split(/\s+/);
    if (words.length > 1) {
      var last = words[words.length - 1];
      if (COLOUR_MAP[last]) return COLOUR_MAP[last];
      var first = words[0];
      if (COLOUR_MAP[first]) return COLOUR_MAP[first];
    }
    // Fall through to CSS named colour (browser will handle 'red', 'orange' etc.)
    return lc;
  }

  // Normalize fabric name (case-insensitive comparison, ignore small differences)
  function normFabric(s) {
    return (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  var parsed = parseTitle(title);
  if (!parsed) return;
  var currentFabricNorm = normFabric(parsed.fabric);
  var currentColour = extractColourFromTitle(title, parsed.base);

  // Fetch other products sharing the base code
  var url = '/search/suggest.json?q=' + encodeURIComponent(parsed.base)
          + '&resources%5Btype%5D=product&resources%5Blimit%5D=20';

  fetch(url)
    .then(function (r) { return r.ok ? r.json() : null; })
    .then(function (data) {
      if (!data) return;
      var products = (data.resources && data.resources.results && data.resources.results.products) || [];
      var siblings = [];
      for (var i = 0; i < products.length; i++) {
        var p = products[i];
        if (!p.title || p.title.indexOf(parsed.base) === -1) continue;
        // Same fabric family? (compare normalized fabric name)
        var pParsed = parseTitle(p.title);
        if (!pParsed) continue;
        // The base has to actually match (not just substring)
        if (pParsed.base !== parsed.base) continue;
        // The fabric name has to be similar enough
        if (normFabric(pParsed.fabric).indexOf(currentFabricNorm) === -1
            && currentFabricNorm.indexOf(normFabric(pParsed.fabric)) === -1) continue;
        var col = extractColourFromTitle(p.title, parsed.base);
        if (!col) continue;
        siblings.push({
          handle: p.handle,
          url: p.url || '/products/' + p.handle,
          colour: col,
          isCurrent: p.handle === currentHandle
        });
      }
      // Dedupe by handle
      var seen = {};
      siblings = siblings.filter(function (s) {
        if (seen[s.handle]) return false;
        seen[s.handle] = true;
        return true;
      });

      // Need at least 2 total (current + 1 other) to be worth showing
      if (siblings.length < 2) return;

      renderSwatches(siblings, currentColour);
    })
    .catch(function () { /* silent */ });

  function renderSwatches(siblings, currentColour) {
    var html = '<div class="product-info__option" style="margin:20px 0;">'
      + '<div class="product-info__option-label" style="display:flex;justify-content:space-between;margin-bottom:8px;">'
      +   '<span style="font-size:.75rem;letter-spacing:.16em;text-transform:uppercase;font-weight:500;">Colour:</span>'
      +   '<span class="value" style="font-size:.8125rem;color:#6B6359;">' + escapeHtml(currentColour || '') + '</span>'
      + '</div>'
      + '<div class="swatches swatches--color" style="display:flex;flex-wrap:wrap;gap:10px;">';
    siblings.forEach(function (s) {
      var cssColour = colourToCss(s.colour);
      var border = s.isCurrent ? '#1A1614' : '#E0D6C5';
      var boxShadow = s.isCurrent ? '0 0 0 2px #FBFAF7 inset' : 'none';
      var tag = s.isCurrent ? 'span' : 'a';
      var attrs = s.isCurrent ? '' : ' href="' + escapeHtml(s.url) + '"';
      html += '<' + tag + attrs
        + ' title="' + escapeHtml(s.colour) + '"'
        + ' style="position:relative;display:inline-block;width:36px;height:36px;border-radius:50%;'
        + 'background:' + cssColour + ';'
        + 'border:2px solid ' + border + ';'
        + 'box-shadow:' + boxShadow + ';'
        + 'text-decoration:none;cursor:' + (s.isCurrent ? 'default' : 'pointer') + ';">'
        + '<span style="position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);">'
        + escapeHtml(s.colour)
        + '</span>'
        + '</' + tag + '>';
    });
    html += '</div></div>';
    container.innerHTML = html;
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
    });
  }
})();
